/**
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {searchProduct, Result} from '@services/search';
import debounce from '@helpers/debounce';

const initSearchbar = () => {
  const {Theme} = window;
  const {searchBar: SearchBarMap} = Theme.selectors;

  const searchCanvas = document.querySelector<HTMLElement>(SearchBarMap.searchCanvas);
  const searchWidget = document.querySelector<HTMLElement>(SearchBarMap.searchWidget);
  const searchDropdown = document.querySelector<HTMLElement>(SearchBarMap.searchDropdown);
  const searchResults = document.querySelector<HTMLElement>(SearchBarMap.searchResults);
  const searchTemplate = document.querySelector<HTMLTemplateElement>(SearchBarMap.searchTemplate);
  const searchInput = document.querySelector<HTMLInputElement>(SearchBarMap.searchInput);
  const searchForm = searchWidget?.querySelector<HTMLFormElement>('form'); // Get the form element
  const searchUrl = searchWidget?.dataset.searchControllerUrl;
  
  let abortController: AbortController | null = null;

  searchCanvas?.addEventListener('hidden.bs.offcanvas', () => {
    if (searchDropdown) {
      searchDropdown.innerHTML = '';
      searchDropdown.classList.add('d-none');
    }
  });

  if (searchWidget && searchInput && searchResults && searchDropdown) {
    searchInput.addEventListener('keydown', debounce(async () => {
      if (searchUrl) {
        // Abort any ongoing request
        if (abortController) {
          abortController.abort('New search initiated');
        }
        
        // Create a new AbortController for the current request
        abortController = new AbortController();
        const signal = abortController.signal;
        
        const products = await searchProduct(searchUrl, searchInput.value, 10, signal);

        if (products.length > 0) {
          searchResults.innerHTML = '';
          products.forEach((e: Result) => {
            const product = <HTMLElement>searchTemplate?.content.cloneNode(true);

            if (product) {
              const productLink = product.querySelector<HTMLAnchorElement>('a');
              const productTitle = product.querySelector<HTMLElement>('p');
              const productImage = product.querySelector<HTMLImageElement>('img');

              if (productLink && productTitle && productImage) {
                productLink.href = e.canonical_url;
                productTitle.innerHTML = e.name;

                if (!e.cover) {
                  productImage.innerHTML = '';
                } else {
                  productImage.src = e.cover.small.url;
                }

                searchResults.append(product);
              }
            }
          });

          searchDropdown.classList.remove('d-none');

          window.addEventListener('click', (e: Event) => {
            if (!searchWidget.contains(<Node>e.target)) {
              searchDropdown.classList.add('d-none');
            }
          });
        } else {
          searchResults.innerHTML = '';
          searchDropdown.classList.add('d-none');
        }
      }
    }, 250));
    
    // Cancel the debounced search when the form is submitted
    searchForm?.addEventListener('submit', () => {
      if (abortController) {
        abortController.abort();
      }
    });
    
    // Clear the search results when the input is cleared
    searchInput.addEventListener('input', () => {
      if (searchInput.value.trim() === '') {
        searchResults.innerHTML = '';
        searchDropdown.classList.add('d-none');
      }
    });
  }
};

export default initSearchbar;
